import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import { Loader2, PackageCheck, ChevronDown, ChevronRight, AlertCircle } from 'lucide-react'
import { apiGet, formatMXN } from '../lib/api'
import { EnvioPanel } from '../components/EnvioPanel'

interface EnvioRow {
  orderId: string
  orderNumber: string
  customerName: string
  shippingStreet: string
  shippingCity: string
  shippingState: string
  shippingZip: string
  carrier: string | null
  paymentMethod: string
  paymentStatus: string
  status: string
  total: number
  createdAt: string
}

interface EnviosData {
  data: EnvioRow[]
  total: number
}

function paymentBadge(paymentStatus: string) {
  if (paymentStatus === 'paid') return <Badge variant="success">Pagado</Badge>
  if (paymentStatus === 'pending') return <Badge variant="warning">Pendiente</Badge>
  if (paymentStatus === 'failed') return <Badge variant="danger">Fallido</Badge>
  return <Badge>{paymentStatus}</Badge>
}

export function AutorizarEnvios() {
  const queryClient = useQueryClient()
  const [vista, setVista] = useState<'pendientes' | 'enviados'>('pendientes')

  const { data, isLoading, isFetching } = useQuery<EnviosData>({
    queryKey: ['envios', vista],
    queryFn: () =>
      apiGet(
        `/api/admin/envios/cola?limit=100${vista === 'enviados' ? '&vista=enviados' : ''}`,
      ),
    refetchOnWindowFocus: false,
  })

  return (
    <>
      <Topbar
        title="Autorizar envíos"
        subtitle={
          data
            ? `${data.total.toLocaleString('es-MX')} ${
                vista === 'enviados' ? 'pedidos con guía generada' : 'pedidos con envío pendiente'
              }`
            : 'Cargando…'
        }
      />
      <div className="p-6">
        <div className="mb-4 inline-flex gap-2">
          <button
            type="button"
            onClick={() => setVista('pendientes')}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              vista === 'pendientes'
                ? 'bg-brand-600 text-white'
                : 'border border-ink-200 bg-white text-ink-700 hover:bg-ink-50'
            }`}
          >
            Pendientes
          </button>
          <button
            type="button"
            onClick={() => setVista('enviados')}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              vista === 'enviados'
                ? 'bg-brand-600 text-white'
                : 'border border-ink-200 bg-white text-ink-700 hover:bg-ink-50'
            }`}
          >
            Enviados
          </button>
        </div>

        <div className="mb-4 flex items-start gap-2 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <p>
            Expande un pedido para revisar la dirección, elegir qué productos enviar (puedes hacer
            envíos parciales) y generar la guía. Un pedido sigue en la cola hasta enviar todos sus
            productos. La tarifa es fija por transportista (Estafeta $160, FedEx $260).
          </p>
        </div>

        <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
          {isLoading && (
            <div className="grid place-items-center py-20 text-ink-500">
              <Loader2 className="size-8 animate-spin" />
              <p className="mt-3 text-sm">Cargando cola de envíos…</p>
            </div>
          )}

          {data && (
            <div className="relative">
              {isFetching && (
                <div className="absolute right-3 top-3 z-10 rounded-full bg-white p-1.5 shadow-md">
                  <Loader2 className="size-4 animate-spin text-brand-600" />
                </div>
              )}
              {data.data.length === 0 ? (
                <div className="grid place-items-center py-20 text-ink-500">
                  <PackageCheck className="size-8 text-emerald-500" />
                  <p className="mt-3 text-sm">
                    {vista === 'enviados'
                      ? 'No hay pedidos con guía generada.'
                      : 'No hay pedidos pendientes de guía.'}
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-ink-100">
                  {data.data.map((row) => (
                    <EnvioRowItem
                      key={row.orderId}
                      row={row}
                      onGenerated={() => queryClient.invalidateQueries({ queryKey: ['envios'] })}
                    />
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

function EnvioRowItem({ row, onGenerated }: { row: EnvioRow; onGenerated: () => void }) {
  const [open, setOpen] = useState(false)

  return (
    <li className="px-4 py-3">
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="grid size-6 shrink-0 place-items-center rounded-md text-ink-500 hover:bg-ink-100"
          title={open ? 'Ocultar' : 'Revisar y generar guía'}
        >
          {open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
        </button>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-ink-900">#{row.orderNumber}</span>
            {paymentBadge(row.paymentStatus)}
            <span className="text-xs text-ink-400">{row.status}</span>
          </div>
          <div className="truncate text-sm text-ink-600">{row.customerName || 'Sin nombre'}</div>
          <div className="truncate text-xs text-ink-500">
            {[row.shippingStreet, row.shippingCity, row.shippingState, row.shippingZip]
              .filter(Boolean)
              .join(', ') || 'Sin dirección'}
          </div>
        </div>

        <div className="text-right text-sm">
          <div className="font-semibold text-ink-900">{formatMXN(row.total)}</div>
          <div className="text-xs text-ink-500">{row.paymentMethod}</div>
        </div>
      </div>

      {open && (
        <div className="mt-3 rounded-xl border border-ink-100 bg-ink-50/60 p-4">
          <EnvioPanel
            orderId={row.orderId}
            showAddress
            initialCarrier={row.carrier === 'fedex' ? 'fedex' : 'estafeta'}
            initialAddress={{
              street: row.shippingStreet || '',
              city: row.shippingCity || '',
              state: row.shippingState || '',
              zip: row.shippingZip || '',
            }}
            onGenerated={onGenerated}
          />
        </div>
      )}
    </li>
  )
}
